import type { ReactNode } from 'react'
import type {
  RadioGroupProps as AriaRadioGroupProps,
  RadioProps,
} from 'react-aria-components'
import {
  Label as AriaLabel,
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  composeRenderProps,
} from 'react-aria-components'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'

export type UIRadioGroupProps = {
  label?: ReactNode
  children?: ReactNode
} & StyleProps &
  AriaRadioGroupProps

const radioGroupStyles = tv({
  base: 'group relative',
})

export function UIRadioGroup(props: UIRadioGroupProps) {
  return (
    <AriaRadioGroup
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        radioGroupStyles({ ...renderProps, className }),
      )}
    >
      {props.label && <AriaLabel>{props.label}</AriaLabel>}
      {props.children}
    </AriaRadioGroup>
  )
}

const radioStyles = tv({
  slots: {
    root: 'group flex',
    radio: 'shrink-0 place-content-center rounded-full me-1 border transition',
  },
  variants: {
    variant: {
      circle: {
        root: 'items-center gap-1 before:size-[1.2em] before:border-2 before:border-black before:rounded-full before:transition-all',
        radio: 'hidden',
      },
      'rounded-label-only': {
        root: 'rounded-lg',
        radio: 'hidden',
      },
      'rounded-radio': {
        root: ['items-center', 'disabled:cursor-not-allowed'],
        radio: [
          'grid items-center border-zinc-400 dark:border-zinc-400 shadow-sm',
          'relative after:absolute after:rounded-full after:size-[10px] after:left-1/2 after:-translate-x-1/2 after:bg-transparent after:transition-colors',
          'group-disabled:border-zinc-200 group-disabled:dark:border-zinc-600',
        ],
      },
    },
    color: {
      theme: {},
      external: {},
    },
    isSelected: {
      true: '',
    },
    size: {
      normal: {
        radio: 'size-4',
      },
      small: {
        radio: 'size-3  after:size-[6px]',
      },
    },
  },
  compoundVariants: [
    {
      variant: 'circle',
      isSelected: true,
      className: { root: 'before:border-feat-brand-100 before:border-[0.4em]' },
    },
    {
      variant: ['rounded-radio'],
      color: 'theme',
      isSelected: true,
      className: {
        radio: [
          'after:bg-purple-600 after:hover:bg-purple-500 group-disabled:after:bg-purple-600/60',
        ],
      },
    },
    {
      variant: ['rounded-radio'],
      color: 'external',
      isSelected: true,
      className: {
        radio: [
          'after:bg-[--radio-active] after:hover:[--radio-active-hover] group-disabled:after:[--radio-active-disabled]',
        ],
      },
    },
  ],
})

type UIRadioProps = VariantProps<typeof radioStyles> &
  RadioProps & {
    radioClassName?: string
  }

export function UIRadio(props: UIRadioProps) {
  const { variant, size, color, className, radioClassName, children, ...rest } =
    props

  const styles = radioStyles({
    variant,
    size,
    color,
  })

  return (
    <AriaRadio
      {...rest}
      className={composeRenderProps(className, (className, renderProps) =>
        styles.root({ ...renderProps, variant, className }),
      )}
    >
      {renderProps => (
        <>
          <div
            className={styles.radio({
              ...renderProps,
              className: radioClassName,
            })}
          ></div>
          {children}
        </>
      )}
    </AriaRadio>
  )
}
